import { ItemTypes, setWidgets } from 'apps/exegesis/pages/notes/dashboard';
import { DarkenSlightly, mobileCutoff } from 'apps/exegesis/styles/themes';
import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'styled-components';
import { BlocksByTagsWidget } from './BlocksByTagsWidget';
import { NotesByTagsWidget } from './NotesByTagsWidget';
import { PinnedTagsWidget } from './PinnedTagsWidget';
import { RandomWidget } from './RandomWidget';

type BaseWidgetProps = {
  id: string;
};

export type BlocksByTagsWidgetProps = {
  type: 'BLOCKS_BY_TAGS_WIDGET';
  tags: string[];
} & BaseWidgetProps;

export type NotesByTagsWidgetProps = {
  type: 'NOTES_BY_TAGS_WIDGET';
  tags: string[];
} & BaseWidgetProps;

export type RandomWidgetProps = {
  type: 'RANDOM_WIDGET';
} & BaseWidgetProps;

export type PinnedTagsWidgetProps = {
  type: 'PINNED_TAGS_WIDGET';
  tags: string[];
} & BaseWidgetProps;

export type Widget =
  | BlocksByTagsWidgetProps
  | NotesByTagsWidgetProps
  | RandomWidgetProps
  | PinnedTagsWidgetProps;
export type WidgetUtilityProps = {
  widgets: Widget[];
  index: number;
};
/*
This file contains a component that provides controls for a widget,
as well as the types for every kind of widget and utilities shared 
by all widgets.
*/

export const WidgetWrapper = (props: {
  widgetData: Widget;
  widgetUtilityProps: WidgetUtilityProps;
}) => {
  let widget;
  const [, drag] = useDrag(
    () => ({
      type: ItemTypes.WIDGET,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      item: {
        index: props.widgetUtilityProps.index,
      },
    }),
    [props]
  );

  switch (props.widgetData.type) {
    case 'BLOCKS_BY_TAGS_WIDGET':
      widget = (
        <BlocksByTagsWidget
          {...props.widgetData}
          {...props.widgetUtilityProps}
        />
      );
      break;
    case 'NOTES_BY_TAGS_WIDGET':
      widget = (
        <NotesByTagsWidget
          {...props.widgetData}
          {...props.widgetUtilityProps}
        />
      );
      break;
    case 'RANDOM_WIDGET':
      widget = (
        <RandomWidget {...props.widgetData} {...props.widgetUtilityProps} />
      );
      break;
    case 'PINNED_TAGS_WIDGET':
      widget = (
        <PinnedTagsWidget {...props.widgetData} {...props.widgetUtilityProps} />
      );
      break;
    default:
      widget = <div>Unknown widget type</div>;
  }
  return <WidgetContainer ref={drag}>{widget}</WidgetContainer>;
};

export const WidgetSlot = ({
  children,
  index,
  widgets,
}: {
  children: React.ReactNode;
  index: number;
  widgets: Widget[];
}) => {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.WIDGET,
      drop: (item: { index: number }) => {
        // splice widget in, shift everything afterwards
        let newWidgets = [...widgets];
        let temp = newWidgets[item.index];
        newWidgets.splice(item.index, 1);
        newWidgets.splice(index, 0, temp);
        setWidgets(newWidgets);
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [index, widgets]
  );

  return (
    <WidgetSlotContainer hover={isOver} ref={drop}>
      {children}
    </WidgetSlotContainer>
  );
};

const WidgetContainer = styled.div`
  width: 400px;
  height: 400px;
  /* margin: 0.75rem 1.5rem 0.75rem 0px; */
  padding: 0.75rem;
  background-color: ${DarkenSlightly};
  border-radius: 10px;

  word-break: break-word;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  animation-name: fadeIn;
  animation-duration: 0.7s;

  @media only screen and (max-width: ${mobileCutoff}) {
    width: 100%;
  }
`;

const WidgetSlotContainer = styled.div<{ hover: boolean }>`
  width: 400px;
  height: 400px;
  margin: 0.75rem 1.5rem 0.75rem 0px;
  background-color: ${(props: { hover: boolean }) =>
    props.hover ? DarkenSlightly : 'transparent'};
`;
